/// Step-by-step walkthrough shown only on the TUTORIAL level.
/// Floats a small banner near the top-center; advances when the
/// player performs the expected action (no timers, no auto-skip).
/// A single "Skip" button in the corner dismisses the whole
/// thing. Once dismissed (manually OR by completing the final
/// step), a localStorage flag prevents it from reappearing.
///
/// The guide reads its progress directly from session-derived
/// state: the AircraftSummary of the only aircraft (RJ123) in
/// the tutorial. We don't subscribe to action events — just
/// observe state and advance when the next condition holds.

import { useEffect, useState } from 'react'
import type { AircraftSummary } from '../state/store'
import {
  logTutorialCompleted,
  logTutorialStarted,
} from '../analytics/AnalyticsManager'

const SEEN_KEY = 'flatout.web.tutorialDone.TUTORIAL'

export function hasSeenTutorial(): boolean {
  return localStorage.getItem(SEEN_KEY) === '1'
}

export function markTutorialSeen(): void {
  if (localStorage.getItem(SEEN_KEY) !== '1') {
    logTutorialCompleted()
  }
  localStorage.setItem(SEEN_KEY, '1')
}

/// Clears the seen-flag so the guide auto-appears on the next
/// TUTORIAL load. Used by the home-screen settings menu's "Replay
/// tutorial" affordance.
export function resetTutorialSeen(): void {
  localStorage.removeItem(SEEN_KEY)
}

interface Step {
  id: string
  text: string
  /// True when this step's expected action has been performed.
  /// Once true, advance to the next step.
  isDone: (s: { selectedId: string | null; aircraft: AircraftSummary | null }) => boolean
}

const STEPS: readonly Step[] = [
  {
    id: 'select',
    text: 'Tap RJ123 to select it.',
    isDone: ({ selectedId, aircraft }) =>
      selectedId === aircraft?.id && aircraft != null,
  },
  {
    id: 'heading',
    text: 'Set heading 220° to turn toward ACY.',
    isDone: ({ aircraft }) => {
      if (!aircraft) return false
      const t = aircraft.targetHeadingDeg
      return t >= 200 && t <= 240
    },
  },
  {
    id: 'altitude',
    text: 'Descend to 3,000 ft for the approach.',
    isDone: ({ aircraft }) => (aircraft ? aircraft.targetAltitudeFt <= 3500 : false),
  },
  {
    id: 'cleared',
    text: 'Once aligned with the runway, tap "Cleared to land".',
    isDone: ({ aircraft }) =>
      aircraft != null &&
      (aircraft.status === 'landing' ||
        aircraft.status === 'landed' ||
        aircraft.status === 'completed'),
  },
  {
    id: 'touchdown',
    text: 'Watch RJ123 touch down. You’ve got this.',
    isDone: ({ aircraft }) =>
      aircraft != null &&
      (aircraft.status === 'landed' || aircraft.status === 'completed'),
  },
]

export function TutorialGuide(props: {
  selectedId: string | null
  aircraft: AircraftSummary | null
  onSkip: () => void
}) {
  const [stepIdx, setStepIdx] = useState(0)
  const step = STEPS[stepIdx]

  useEffect(() => {
    logTutorialStarted()
  }, [])

  useEffect(() => {
    if (!step) return
    if (step.isDone({ selectedId: props.selectedId, aircraft: props.aircraft })) {
      // Advance — final-step completion auto-marks the guide done.
      const next = stepIdx + 1
      if (next >= STEPS.length) {
        markTutorialSeen()
        props.onSkip()
      } else {
        setStepIdx(next)
      }
    }
  }, [props.selectedId, props.aircraft, stepIdx, step, props])

  if (!step) return null

  return (
    <div className="pointer-events-none absolute left-1/2 top-2 z-30 flex w-[420px] max-w-[calc(100vw-1rem)] -translate-x-1/2 flex-col gap-1.5 md:top-3">
      <div className="pointer-events-auto rounded-lg bg-emerald-900/90 px-3 py-2 text-sm text-white shadow-lg ring-1 ring-emerald-400/40 backdrop-blur">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-200">
            Step {stepIdx + 1} / {STEPS.length}
          </span>
          <button
            onClick={() => {
              markTutorialSeen()
              props.onSkip()
            }}
            className="text-[11px] text-emerald-200/80 hover:text-white"
          >
            Skip tutorial
          </button>
        </div>
        <div className="mt-0.5 font-medium leading-snug">{step.text}</div>
      </div>
    </div>
  )
}
